/**
 * IM 账号 ID 的反解工具。
 *
 * 服务端拼出来的 IM 账号 ID 形如 `userId_customerId_userType`（三段）或
 * `userId_id`（两段，客服侧）。非生产环境还会在最前面套一段环境前缀做租户隔离，
 * 于是实际 ID 变成 `t_132300_1_40` —— 直接 split('_') 取第 0 段会拿到 'PREFIX'
 * 而不是 userId：客服端收消息时 `+fromUserId` 变成 NaN，消息一条都对不上、不渲染；
 * 直播控制台禁言会把前缀当 userId 发给后端。
 *
 * 前缀是服务端配的（`IM.userIdPrefix`），前端不知道具体值，所以不能按字面剥。
 * 这里按「业务字段全是数字」这一点来剥：丢掉开头连续的非纯数字段。
 * 生产前缀为空，结果与直接 split 完全一致。
 *
 * 约束：环境前缀不能取纯数字（否则和 userId 分不开）。目前用的是 `t_`。
 */

/**
 * 反解出 IM 账号 ID 里的业务字段，已剥掉环境前缀。
 * 拿不到有效字段时返回空数组，让调用方走「解析不出来」的分支。
 *
 * @param {string} imUserId IM 账号 ID
 * @returns {string[]} 业务字段，形如 ['132300', '1', '40']
 */
export function imIdRawParts(imUserId) {
  if (!imUserId || typeof imUserId !== 'string') {
    return []
  }
  const parts = imUserId.split('_').map(seg => seg.trim())
  let start = 0
  while (start < parts.length && !/^\d+$/.test(parts[start])) {
    start++
  }
  return parts.slice(start)
}

/**
 * 取 IM 账号 ID 里的 userId 段（已剥前缀）。取不到返回原值，调用方自行兜底。
 *
 * @param {string} imUserId IM 账号 ID
 * @returns {string} userId
 */
export function imIdUserId(imUserId) {
  const parts = imIdRawParts(imUserId)
  return parts.length > 0 ? parts[0] : imUserId
}

/**
 * 从一个 IM 账号 ID 里取出环境前缀（含结尾的下划线），没有前缀返回空串。
 *
 * 前端不知道服务端配的前缀值，但自己登录 IM 用的那个账号 ID 是服务端给的、
 * 已经带前缀，所以可以拿它当参照物反推出前缀，再去拼别人的 ID。
 *
 * @param {string} imUserId 参照用的 IM 账号 ID，形如 t_132212_2_40
 * @returns {string} 前缀，形如 't_'；没有前缀返回 ''
 */
export function imIdPrefixOf(imUserId) {
  if (!imUserId || typeof imUserId !== 'string') {
    return ''
  }
  const parts = imUserId.split('_')
  let start = 0
  while (start < parts.length && !/^\d+$/.test(parts[start])) {
    start++
  }
  return start > 0 ? parts.slice(0, start).join('_') + '_' : ''
}
